
import {
  BorrowerAction,
  CompleteActionResponse,
  GiftLetterInfo,
  LoadAppraisalResponse,
  LoadBorrowerActionsResponse,
  LoadInsuranceResponse,
  RequestAction,
  SubmitGiftLetterResponse,
  SubmitInsuranceResponse,
  SubmitPaymentResponse,
} from '@ld.consumer-portal/models';
import { EndpointScenarioMap } from './mock.types';

export const borrowerActionsScenarios: EndpointScenarioMap<LoadBorrowerActionsResponse> = {
  default: {
    actions: [
      {
        actionId: 'action-001',
        borrowerId: 'borrower-001',
        name: 'Pay Appraisal Fee',
        description: 'Payment required to order home appraisal',
        requestedOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.Payment,
        actionOrder: 1,
        feeInfo: { amount: 550, feeType: 'appraisal', isPaid: false },
      },
      {
        actionId: 'action-002',
        borrowerId: 'borrower-001',
        name: 'Homeowners Insurance Quotes',
        description: 'View and select homeowners insurance quotes',
        requestedOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.ViewQuote,
        actionOrder: 2,
      },
    ],
    showApprovedWithConditions: false,
  },

  'many-actions': {
    actions: [
      {
        actionId: 'action-001',
        borrowerId: 'borrower-001',
        name: 'Pay Appraisal Fee',
        description: 'Payment of $550 required to order appraisal',
        requestedOn: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.Payment,
        actionOrder: 1,
        feeInfo: { amount: 550, feeType: 'appraisal', isPaid: false },
      },
      {
        actionId: 'action-002',
        borrowerId: 'borrower-001',
        name: 'Homeowners Insurance Quotes',
        description: 'View and select homeowners insurance quotes',
        requestedOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.ViewQuote,
        actionOrder: 2,
      },
      {
        actionId: 'action-003',
        borrowerId: 'borrower-001',
        name: 'Make a Payment',
        description: 'Make a payment towards your loan',
        requestedOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.MakeAPayment,
        actionOrder: 3,
      },
      {
        actionId: 'action-004',
        borrowerId: 'borrower-001',
        name: 'Review Conditions',
        description: 'Review and acknowledge loan conditions',
        requestedOn: new Date().toISOString(),
        requestAction: RequestAction.Condition,
        actionOrder: 4,
      },
    ],
    showApprovedWithConditions: true,
  },

  'some-completed': {
    actions: [
      {
        actionId: 'action-001',
        borrowerId: 'borrower-001',
        name: 'Pay Appraisal Fee',
        requestedOn: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.Payment,
        actionOrder: 1,
        feeInfo: { amount: 550, feeType: 'appraisal', isPaid: true },
      },
      {
        actionId: 'action-002',
        borrowerId: 'borrower-001',
        name: 'Homeowners Insurance Quotes',
        description: 'View and select homeowners insurance quotes',
        requestedOn: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        requestAction: RequestAction.ViewQuote,
        actionOrder: 2,
      },
    ],
    showApprovedWithConditions: false,
  },

  'all-completed': {
    actions: [],
    showApprovedWithConditions: false,
  },

  'no-actions': {
    actions: [],
    showApprovedWithConditions: false,
  },
};

export const borrowerActionScenarios: EndpointScenarioMap<BorrowerAction> = {
  default: {
    actionId: 'action-001',
    borrowerId: 'borrower-001',
    name: 'Pay Appraisal Fee',
    description: 'Payment of $550 is required to order your home appraisal.',
    requestedOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    requestAction: RequestAction.Payment,
    actionOrder: 1,
    feeInfo: { amount: 550, feeType: 'appraisal', isPaid: false },
  },
  completed: {
    actionId: 'action-001',
    borrowerId: 'borrower-001',
    name: 'Pay Appraisal Fee',
    requestedOn: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    requestAction: RequestAction.Payment,
    actionOrder: 1,
    feeInfo: { amount: 550, feeType: 'appraisal', isPaid: true },
  },
};

export const completeActionScenarios: EndpointScenarioMap<CompleteActionResponse> = {
  default: {
    success: true,
    action: {
      actionId: 'action-001',
      borrowerId: 'borrower-001',
      name: 'Pay Appraisal Fee',
      requestedOn: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      requestAction: RequestAction.Payment,
      actionOrder: 1,
      feeInfo: { amount: 550, feeType: 'appraisal', isPaid: true },
    },
  },
  failed: {
    success: false,
    action: {
      actionId: 'action-001',
      borrowerId: 'borrower-001',
      name: 'Pay Appraisal Fee',
      requestedOn: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      requestAction: RequestAction.Payment,
      actionOrder: 1,
      feeInfo: { amount: 550, feeType: 'appraisal', isPaid: false },
    },
  },
};

export const submitPaymentScenarios: EndpointScenarioMap<SubmitPaymentResponse> = {
  default: {
    success: true,
    payment: {
      paymentId: 'pay-001',
      loanId: 'loan-001',
      amount: 550,
      paymentType: 'appraisal',
      status: 'completed',
      processedDate: new Date().toISOString(),
      confirmationNumber: 'CONF-2024-001234',
    },
    confirmationNumber: 'CONF-2024-001234',
  },
  failed: {
    success: false,
    payment: {
      paymentId: 'pay-002',
      loanId: 'loan-001',
      amount: 550,
      paymentType: 'appraisal',
      status: 'failed',
    },
  },
  pending: {
    success: true,
    payment: {
      paymentId: 'pay-003',
      loanId: 'loan-001',
      amount: 550,
      paymentType: 'appraisal',
      status: 'processing',
      scheduledDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    },
  },
};

export const insuranceScenarios: EndpointScenarioMap<LoadInsuranceResponse> = {
  default: {
    insurances: [],
  },
  'with-insurance': {
    insurances: [
      {
        insuranceId: 'ins-001',
        loanId: 'loan-001',
        provider: 'State Farm',
        policyNumber: 'SF-12345678',
        coverageAmount: 500000,
        premium: 1200,
        effectiveDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        expirationDate: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'homeowners',
        status: 'active',
        agentName: 'John Agent',
        agentPhone: '555-123-4567',
        agentEmail: 'john.agent@statefarm.com',
      },
    ],
  },
  'multiple-policies': {
    insurances: [
      {
        insuranceId: 'ins-001',
        loanId: 'loan-001',
        provider: 'State Farm',
        policyNumber: 'SF-12345678',
        coverageAmount: 500000,
        premium: 1200,
        effectiveDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        expirationDate: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'homeowners',
        status: 'active',
      },
      {
        insuranceId: 'ins-002',
        loanId: 'loan-001',
        provider: 'FEMA NFIP',
        policyNumber: 'NFIP-87654321',
        coverageAmount: 250000,
        premium: 800,
        effectiveDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        expirationDate: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'flood',
        status: 'active',
      },
    ],
  },
};

export const submitInsuranceScenarios: EndpointScenarioMap<SubmitInsuranceResponse> = {
  default: {
    success: true,
    insurance: {
      insuranceId: 'ins-001',
      loanId: 'loan-001',
      provider: 'State Farm',
      policyNumber: 'SF-12345678',
      coverageAmount: 500000,
      type: 'homeowners',
      status: 'pending',
    },
  },
  failed: {
    success: false,
    insurance: {
      insuranceId: 'ins-002',
      loanId: 'loan-001',
      provider: 'Unknown',
      type: 'homeowners',
      status: 'pending',
    },
  },
};

export const giftLettersScenarios: EndpointScenarioMap<GiftLetterInfo[]> = {
  default: [],
  'with-gift': [
    {
      giftId: 'gift-001',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Robert Smith',
      donorRelationship: 'Father',
      amount: 25000,
      status: 'pending',
    },
  ],
  'multiple-gifts': [
    {
      giftId: 'gift-001',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Robert Smith',
      donorRelationship: 'Father',
      amount: 25000,
      status: 'verified',
      signedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      giftId: 'gift-002',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Mary Smith',
      donorRelationship: 'Mother',
      amount: 15000,
      status: 'pending',
    },
  ],
  verified: [
    {
      giftId: 'gift-001',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Robert Smith',
      donorRelationship: 'Father',
      amount: 50000,
      status: 'verified',
      signedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
};

export const submitGiftLetterScenarios: EndpointScenarioMap<SubmitGiftLetterResponse> = {
  default: {
    success: true,
    giftLetter: {
      giftId: 'gift-001',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Robert Smith',
      donorRelationship: 'Father',
      amount: 25000,
      status: 'pending',
    },
  },
  failed: {
    success: false,
    giftLetter: {
      giftId: 'gift-002',
      loanId: 'loan-001',
      borrowerId: 'borrower-001',
      donorName: 'Unknown',
      donorRelationship: 'Unknown',
      amount: 0,
      status: 'pending',
    },
  },
};

export const appraisalScenarios: EndpointScenarioMap<LoadAppraisalResponse> = {
  default: {
    appraisal: null,
  },
  pending: {
    appraisal: {
      appraisalId: 'apr-001',
      loanId: 'loan-001',
      status: 'pending',
      notes: 'Appraisal fee payment received. Awaiting appraiser assignment.',
    },
  },
  scheduled: {
    appraisal: {
      appraisalId: 'apr-001',
      loanId: 'loan-001',
      status: 'scheduled',
      scheduledDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      appraiserName: 'ABC Appraisals',
      appraiserPhone: '555-789-0123',
      estimatedValue: 550000,
    },
  },
  'in-progress': {
    appraisal: {
      appraisalId: 'apr-001',
      loanId: 'loan-001',
      status: 'in_progress',
      scheduledDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      appraiserName: 'ABC Appraisals',
      appraiserPhone: '555-789-0123',
      notes: 'Inspection completed. Report being prepared.',
    },
  },
  completed: {
    appraisal: {
      appraisalId: 'apr-001',
      loanId: 'loan-001',
      status: 'completed',
      scheduledDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      completedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      appraiserName: 'ABC Appraisals',
      estimatedValue: 550000,
      actualValue: 545000,
    },
  },
  disputed: {
    appraisal: {
      appraisalId: 'apr-001',
      loanId: 'loan-001',
      status: 'disputed',
      completedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      appraiserName: 'ABC Appraisals',
      estimatedValue: 550000,
      actualValue: 480000,
      notes: 'Appraisal value disputed by borrower. Review in progress.',
    },
  },
};
